import { useState, useCallback } from "react";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import { toast } from "sonner";
import { formatINR } from "../lib/utils-format";
import {
    TrendingUp, Users, IndianRupee, Wallet, ShieldCheck,
    ChevronDown, Download, RefreshCw,
} from "lucide-react";

/* ── helpers ────────────────────────────────────────────────────────── */
function today() {
    return new Date().toISOString().slice(0, 10);
}
function daysAgo(n) {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
}
function startOfWeek() {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay() + 1); // Monday
    return d.toISOString().slice(0, 10);
}
function startOfMonth() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function startOfLastMonth() {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 10);
}
function endOfLastMonth() {
    const d = new Date();
    d.setDate(0); // last day of previous month
    return d.toISOString().slice(0, 10);
}
function threeMonthsAgo() {
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    d.setDate(1);
    return d.toISOString().slice(0, 10);
}

const PRESETS = [
    { label: "Today",          from: () => today(),          to: () => today(),          group: "day"   },
    { label: "Yesterday",      from: () => daysAgo(1),       to: () => daysAgo(1),       group: "day"   },
    { label: "This Week",      from: () => startOfWeek(),    to: () => today(),           group: "day"   },
    { label: "This Month",     from: () => startOfMonth(),   to: () => today(),           group: "day"   },
    { label: "Last Month",     from: () => startOfLastMonth(),to: () => endOfLastMonth(), group: "day"   },
    { label: "Last 3 Months",  from: () => threeMonthsAgo(), to: () => today(),           group: "month" },
];

/* ── small components ───────────────────────────────────────────────── */
function SummaryCard({ label, value, sub, color = "navy", icon: Icon }) {
    const schemes = {
        navy:    "bg-[#1B2B6B] text-white",
        blue:    "bg-[#2172E5] text-white",
        teal:    "bg-[#10B1B6] text-white",
        emerald: "bg-emerald-600 text-white",
        amber:   "bg-amber-50 border border-amber-200 text-amber-800",
        slate:   "bg-white border border-slate-200 text-slate-800",
    };
    const labelCls = color === "amber" ? "text-amber-600" : color === "slate" ? "text-slate-500" : "text-white/70";
    const valueCls = color === "amber" ? "text-amber-700" : color === "slate" ? "text-[#1B2B6B]" : "text-white";
    const subCls   = color === "amber" ? "text-amber-500" : color === "slate" ? "text-slate-400" : "text-white/50";
    return (
        <div className={`rounded-xl p-5 ${schemes[color]}`}>
            <div className={`flex items-center gap-2 mb-2 text-xs font-bold uppercase tracking-wider ${labelCls}`}>
                {Icon && <Icon className="w-3.5 h-3.5" />}
                {label}
            </div>
            <div className={`font-display font-black text-2xl leading-tight ${valueCls}`}>{value}</div>
            {sub && <div className={`text-xs mt-1 ${subCls}`}>{sub}</div>}
        </div>
    );
}

function Pill({ active, onClick, children }) {
    return (
        <button
            onClick={onClick}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                active
                    ? "bg-[#1B2B6B] text-white border-[#1B2B6B]"
                    : "bg-white text-slate-600 border-slate-200 hover:border-[#2172E5] hover:text-[#2172E5]"
            }`}
        >
            {children}
        </button>
    );
}

/* ── CSV export ─────────────────────────────────────────────────────── */
function exportCSV(timeline, fromDate, toDate) {
    const headers = [
        "Period", "New Members", "Membership Revenue",
        "Direct Bonus Gross", "Pairing Bonus Gross",
        "Member Direct Net", "Member Pairing Net", "Biz Pairing Net",
        "Admin Charges", "TDS Collected", "Travel Fund",
        "Total Member Earned", "Total Biz Income", "Withdrawals",
    ];
    const rows = timeline.map(r => [
        r.label, r.new_members, r.membership_revenue,
        r.gross_direct, r.gross_pairing,
        r.member_direct_net, r.member_pairing_net, r.biz_pairing_net,
        r.admin_charges, r.tds_collected, r.travel_fund,
        r.total_member_earned, r.total_biz_income, r.withdrawals,
    ]);
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url;
    a.download = `zenwyxon-income-${fromDate}-to-${toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
}

/* ── main page ──────────────────────────────────────────────────────── */
export default function AdminReports() {
    const [fromDate,  setFromDate]  = useState(startOfMonth());
    const [toDate,    setToDate]    = useState(today());
    const [groupBy,   setGroupBy]   = useState("day");
    const [data,      setData]      = useState(null);
    const [loading,   setLoading]   = useState(false);
    const [activePreset, setActivePreset] = useState("This Month");

    const run = useCallback(async (from, to, group) => {
        setLoading(true);
        setData(null);
        try {
            const { data: res } = await api.get("/admin/reports/income", {
                params: { from_date: from, to_date: to, group_by: group },
            });
            setData(res);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load report");
        } finally {
            setLoading(false);
        }
    }, []);

    const applyPreset = (p) => {
        const f = p.from(), t = p.to(), g = p.group;
        setFromDate(f);
        setToDate(t);
        setGroupBy(g);
        setActivePreset(p.label);
        run(f, t, g);
    };

    const runCustom = () => {
        setActivePreset(null);
        run(fromDate, toDate, groupBy);
    };

    const s = data?.summary;
    const tl = data?.timeline || [];

    return (
        <AppShell admin>
            {/* ── header ── */}
            <div className="mb-6">
                <div className="overline">Admin</div>
                <h1 className="font-display font-black text-3xl tracking-tighter text-slate-900 mt-2">
                    Income Reports
                </h1>
                <p className="text-slate-500 text-sm mt-1">
                    Daily, weekly, or monthly breakdown of all platform income and bonus payments.
                </p>
            </div>

            {/* ── controls ── */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-6">
                {/* Quick presets */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                    {PRESETS.map(p => (
                        <Pill key={p.label} active={activePreset === p.label} onClick={() => applyPreset(p)}>
                            {p.label}
                        </Pill>
                    ))}
                </div>

                {/* Custom range + group + run */}
                <div className="flex flex-wrap items-end gap-3">
                    <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">From</label>
                        <input
                            type="date" value={fromDate}
                            onChange={e => { setFromDate(e.target.value); setActivePreset(null); }}
                            className="h-9 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[#2172E5]"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">To</label>
                        <input
                            type="date" value={toDate}
                            onChange={e => { setToDate(e.target.value); setActivePreset(null); }}
                            className="h-9 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[#2172E5]"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Group by</label>
                        <div className="flex gap-1">
                            {["day", "week", "month"].map(g => (
                                <button
                                    key={g}
                                    onClick={() => setGroupBy(g)}
                                    className={`h-9 px-3 rounded-lg text-xs font-semibold border transition-colors capitalize ${
                                        groupBy === g
                                            ? "bg-[#1B2B6B] text-white border-[#1B2B6B]"
                                            : "bg-white text-slate-600 border-slate-200 hover:border-[#2172E5]"
                                    }`}
                                >
                                    {g}
                                </button>
                            ))}
                        </div>
                    </div>
                    <button
                        onClick={runCustom}
                        disabled={loading}
                        className="h-9 px-5 rounded-lg bg-[#2172E5] hover:bg-[#1B2B6B] text-white text-sm font-semibold disabled:opacity-50 transition-colors flex items-center gap-2"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                        {loading ? "Loading…" : "Run Report"}
                    </button>
                    {data && (
                        <button
                            onClick={() => exportCSV(tl, fromDate, toDate)}
                            className="h-9 px-4 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                        >
                            <Download className="w-3.5 h-3.5" /> Export CSV
                        </button>
                    )}
                </div>
            </div>

            {!data && !loading && (
                <div className="text-center py-20 text-slate-400 text-sm">
                    Select a date range and click <span className="font-semibold">Run Report</span> to see income data.
                </div>
            )}
            {loading && (
                <div className="text-center py-20 text-slate-400 text-sm">Loading report…</div>
            )}

            {data && (
                <>
                    {/* ── summary cards ── */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <SummaryCard
                            label="New Members"
                            value={s.new_members}
                            sub={`₹${(s.membership_revenue).toLocaleString("en-IN")} revenue`}
                            color="navy"
                            icon={Users}
                        />
                        <SummaryCard
                            label="Total Member Net"
                            value={formatINR(s.total_member_net)}
                            sub={`Direct ₹${s.member_direct_net.toLocaleString("en-IN")} + Pairing ₹${s.member_pairing_net.toLocaleString("en-IN")} + Travel ₹${s.travel_fund_credited.toLocaleString("en-IN")}`}
                            color="blue"
                            icon={Wallet}
                        />
                        <SummaryCard
                            label="Business Income"
                            value={formatINR(s.total_biz_income)}
                            sub={`Admin ₹${s.admin_charges.toLocaleString("en-IN")} · TDS ₹${s.tds_collected.toLocaleString("en-IN")} · Pairs ₹${s.biz_pairing_net.toLocaleString("en-IN")}`}
                            color="teal"
                            icon={TrendingUp}
                        />
                        <SummaryCard
                            label="Travel Fund Credited"
                            value={formatINR(s.travel_fund_credited)}
                            sub="Accumulated across all members"
                            color="slate"
                            icon={IndianRupee}
                        />
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                        <SummaryCard
                            label="Direct Bonus Gross"
                            value={formatINR(s.gross_direct_bonus)}
                            sub={`Net to members: ${formatINR(s.member_direct_net)}`}
                            color="slate"
                        />
                        <SummaryCard
                            label="Pairing Bonus Gross"
                            value={formatINR(s.gross_pairing_bonus)}
                            sub={`Member net: ${formatINR(s.member_pairing_net)} · Biz: ${formatINR(s.biz_pairing_net)}`}
                            color="slate"
                        />
                        <SummaryCard
                            label="Admin Charges (15%)"
                            value={formatINR(s.admin_charges)}
                            sub="From all bonus events"
                            color="amber"
                            icon={ShieldCheck}
                        />
                        <SummaryCard
                            label="TDS Collected (5%)"
                            value={formatINR(s.tds_collected)}
                            sub="Tax deducted at source"
                            color="amber"
                        />
                    </div>

                    {/* ── top earners ── */}
                    {data.top_earners?.length > 0 && (
                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6 overflow-hidden">
                            <div className="px-5 py-3 border-b border-slate-100">
                                <h2 className="font-display font-bold text-base text-slate-900">Top Earners This Period</h2>
                            </div>
                            <div className="divide-y divide-slate-50">
                                {data.top_earners.map((e, i) => (
                                    <div key={e.user_id} className="flex items-center justify-between px-5 py-3">
                                        <div className="flex items-center gap-3">
                                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black ${
                                                i === 0 ? "bg-amber-100 text-amber-700" :
                                                i === 1 ? "bg-slate-100 text-slate-600" :
                                                i === 2 ? "bg-orange-50 text-orange-600" : "bg-slate-50 text-slate-400"
                                            }`}>{i + 1}</span>
                                            <div>
                                                <div className="font-semibold text-sm text-slate-900">{e.name}</div>
                                                <div className="font-mono text-[10px] text-[#2172E5]">{e.code}</div>
                                            </div>
                                        </div>
                                        <div className="font-mono font-black text-sm text-[#1B2B6B]">{formatINR(e.earned)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ── timeline table ── */}
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
                        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                            <h2 className="font-display font-bold text-base text-slate-900">
                                {groupBy === "day" ? "Daily" : groupBy === "week" ? "Weekly" : "Monthly"} Breakdown
                                <span className="ml-2 text-xs font-normal text-slate-400">({tl.length} periods)</span>
                            </h2>
                        </div>
                        {tl.length === 0 ? (
                            <div className="text-center py-10 text-slate-400 text-sm">No transactions in this period.</div>
                        ) : (
                        <table className="w-full text-sm" style={{ fontVariantNumeric: "tabular-nums" }}>
                            <thead>
                                <tr className="bg-gradient-to-r from-[#1B2B6B] to-[#2A3D82] text-white">
                                    <th className="text-left font-semibold py-3 px-4 text-xs uppercase tracking-wider whitespace-nowrap">Period</th>
                                    <th className="text-right font-semibold py-3 px-3 text-xs uppercase tracking-wider whitespace-nowrap">Members</th>
                                    <th className="text-right font-semibold py-3 px-3 text-xs uppercase tracking-wider whitespace-nowrap">Membership Rev.</th>
                                    <th className="text-right font-semibold py-3 px-3 text-xs uppercase tracking-wider whitespace-nowrap">Direct Gross</th>
                                    <th className="text-right font-semibold py-3 px-3 text-xs uppercase tracking-wider whitespace-nowrap">Member Direct Net</th>
                                    <th className="text-right font-semibold py-3 px-3 text-xs uppercase tracking-wider whitespace-nowrap">Pairing Gross</th>
                                    <th className="text-right font-semibold py-3 px-3 text-xs uppercase tracking-wider whitespace-nowrap">Member Pair Net</th>
                                    <th className="text-right font-semibold py-3 px-3 text-xs uppercase tracking-wider whitespace-nowrap">Biz Pair Net</th>
                                    <th className="text-right font-semibold py-3 px-3 text-xs uppercase tracking-wider whitespace-nowrap">Admin (15%)</th>
                                    <th className="text-right font-semibold py-3 px-3 text-xs uppercase tracking-wider whitespace-nowrap">TDS (5%)</th>
                                    <th className="text-right font-semibold py-3 px-3 text-xs uppercase tracking-wider whitespace-nowrap">Travel Fund</th>
                                    <th className="text-right font-semibold py-3 px-4 text-xs uppercase tracking-wider whitespace-nowrap">Biz Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tl.map((row, i) => (
                                    <tr key={row.period} className={`border-b border-slate-50 hover:bg-slate-50 transition-colors ${i % 2 === 0 ? "" : "bg-slate-50/40"}`}>
                                        <td className="py-3 px-4 font-semibold text-slate-800 whitespace-nowrap">{row.label}</td>
                                        <td className="py-3 px-3 text-right text-slate-600">{row.new_members || "—"}</td>
                                        <td className="py-3 px-3 text-right text-slate-700">{row.membership_revenue ? formatINR(row.membership_revenue) : <span className="text-slate-300">—</span>}</td>
                                        <td className="py-3 px-3 text-right text-slate-600">{row.gross_direct ? formatINR(row.gross_direct) : <span className="text-slate-300">—</span>}</td>
                                        <td className="py-3 px-3 text-right font-semibold text-[#2172E5]">{row.member_direct_net ? formatINR(row.member_direct_net) : <span className="text-slate-300">—</span>}</td>
                                        <td className="py-3 px-3 text-right text-slate-600">{row.gross_pairing ? formatINR(row.gross_pairing) : <span className="text-slate-300">—</span>}</td>
                                        <td className="py-3 px-3 text-right font-semibold text-[#2172E5]">{row.member_pairing_net ? formatINR(row.member_pairing_net) : <span className="text-slate-300">—</span>}</td>
                                        <td className="py-3 px-3 text-right text-[#10B1B6] font-semibold">{row.biz_pairing_net ? formatINR(row.biz_pairing_net) : <span className="text-slate-300">—</span>}</td>
                                        <td className="py-3 px-3 text-right text-amber-600">{row.admin_charges ? formatINR(row.admin_charges) : <span className="text-slate-300">—</span>}</td>
                                        <td className="py-3 px-3 text-right text-amber-600">{row.tds_collected ? formatINR(row.tds_collected) : <span className="text-slate-300">—</span>}</td>
                                        <td className="py-3 px-3 text-right text-slate-500">{row.travel_fund ? formatINR(row.travel_fund) : <span className="text-slate-300">—</span>}</td>
                                        <td className="py-3 px-4 text-right font-black text-[#1B2B6B]">{row.total_biz_income ? formatINR(row.total_biz_income) : <span className="text-slate-300">—</span>}</td>
                                    </tr>
                                ))}
                            </tbody>
                            {/* Totals row */}
                            <tfoot>
                                <tr className="bg-gradient-to-r from-[#1B2B6B] to-[#2A3D82] text-white font-bold text-sm">
                                    <td className="py-3 px-4 text-blue-200 text-xs">TOTAL ({tl.length})</td>
                                    <td className="py-3 px-3 text-right">{s.new_members}</td>
                                    <td className="py-3 px-3 text-right">{formatINR(s.membership_revenue)}</td>
                                    <td className="py-3 px-3 text-right">{formatINR(s.gross_direct_bonus)}</td>
                                    <td className="py-3 px-3 text-right">{formatINR(s.member_direct_net)}</td>
                                    <td className="py-3 px-3 text-right">{formatINR(s.gross_pairing_bonus)}</td>
                                    <td className="py-3 px-3 text-right">{formatINR(s.member_pairing_net)}</td>
                                    <td className="py-3 px-3 text-right">{formatINR(s.biz_pairing_net)}</td>
                                    <td className="py-3 px-3 text-right">{formatINR(s.admin_charges)}</td>
                                    <td className="py-3 px-3 text-right">{formatINR(s.tds_collected)}</td>
                                    <td className="py-3 px-3 text-right">{formatINR(s.travel_fund_credited)}</td>
                                    <td className="py-3 px-4 text-right">{formatINR(s.total_biz_income)}</td>
                                </tr>
                            </tfoot>
                        </table>
                        )}
                    </div>
                </>
            )}
        </AppShell>
    );
}
